/**
 * main-readout-content.ts — authored narrative for the /main "Summary" readout tab.
 *
 * WHAT THIS IS
 * ------------
 * The Summary tab pairs a HUMAN-authored exec narrative with LIVE Salesforce
 * numbers. This file is the human half: the framing, the arc, the judgement
 * calls an exec reads to understand "what the numbers mean". The numbers
 * themselves are never hard-coded here — they are injected at render time from
 * the live /api/main/scorecard payload via {{token}} placeholders (see
 * main-readout.ts for the token grammar), so the prose can be reviewed on a
 * cadence while the figures stay current to the last cache refresh.
 *
 * HOW TO UPDATE (each reporting cycle)
 * ------------------------------------
 *   1. Re-read the sections below and adjust the framing to match reality
 *      (e.g. if a channel changed, if a target moved, if the story shifted).
 *   2. Bump READOUT_REVIEWED_ON to the date you reviewed it.
 *   3. Leave the {{tokens}} alone unless a metric id changed — they self-update.
 *
 * TOKEN GRAMMAR (resolved by main-readout.ts):
 * Each kind mirrors the EXACT formatter its tile uses (see main-readout.ts) so a
 * number reads identically here and on the tab:
 *   {{MAIN-24}}              → single-metric value, formatted by its unit
 *   {{MAIN-24|target}}       → that metric's target value
 *   {{MAIN-24|pctTarget}}    → single-metric value as % of target (tile's chip)
 *   {{MAIN-04|count}}        → count activity headline (formatCountInt)
 *   {{MAIN-32|total}}        → generic bucketed headline (≥$1M as $, else int)
 *   {{MAIN-12|qtrUsd}}       → unit-quarter currency headline ("$82k")
 *   {{MAIN-20|days}}         → unit-quarter days headline ("243.0 days")
 *   {{MAIN-04|wow}}          → WoW change as the tile's pill shows it: an
 *                              ABSOLUTE count ("up 120" / "down 80" / "flat")
 *   {{MAIN-04|mom}}          → MoM change, same absolute-count form
 *   {{MAIN-21|trend}}        → snapshot direction, e.g. "down 8% on the week"
 *   MAIN-17 FY pacing (derived from the MAIN-48 series the way MainPacingTile
 *   does — coverage target is NET of Closed-Won, matching the tile headline):
 *   {{MAIN-17|fyActual}}     → OPEN pipeline for the FY to date (cumulative by close month)
 *   {{MAIN-17|fyTarget}}     → coverage target the tile leads with (net of CW)
 *   {{MAIN-17|fyGrossTarget}}→ gross 3× FY target before CW is backed out
 *   {{MAIN-17|fyCoverage}}   → coverage as % of the coverage target
 *   {{MAIN-17|fyClosedWon}}  → Closed-Won FY already booked
 *   {{MAIN-17|fyPaceWord}}   → "above the coverage target" / "below the coverage target"
 * Any token whose data is missing/null renders an em dash "—" — the readout
 * never fabricates a number.
 */

/** Date the narrative framing below was last reviewed by a human. */
export const READOUT_REVIEWED_ON = '2026-09-21';

/** Short "how to read this" preamble shown at the top of the Summary tab. */
export const READOUT_INTRO =
  'This is the one-screen readout of the Main sales dashboard. Every figure below is ' +
  'pulled live from Salesforce and updates with each refresh; the framing around them ' +
  'is reviewed each reporting cycle. It answers three questions in order — are we ' +
  'generating real pipeline, are we closing better deals, and are we spending ' +
  'efficiently — and each section links to the tab where you can drill into the source ' +
  'records.';

export interface ReadoutHighlight {
  /** Short chip label, e.g. "Outbound (30d)". */
  label: string;
  /** Token spec resolved against the live scorecard, e.g. "MAIN-04|count". */
  token: string;
}

export interface ReadoutSection {
  id: string;
  /** The exec question this section answers — rendered as the heading. */
  question: string;
  /** One-line framing under the heading. */
  standfirst: string;
  /** Which /main tab this maps to, for the "see the detail" affordance. */
  tab: 'sales' | 'marketing' | 'roi';
  tabLabel: string;
  /** Authored paragraphs; may embed {{token}} placeholders. */
  body: string[];
  /**
   * One executive-level, strategic sentence on what the data MEANS — a durable
   * GTM lens (not a stale factual claim), in the spirit of Winning by Design's
   * revenue-architecture framing. Kept token-free so it can't drift out of sync.
   */
  strategic: string;
  /** Compact key-number strip rendered under the prose. */
  highlights: ReadoutHighlight[];
}

export const READOUT_SECTIONS: ReadoutSection[] = [
  {
    id: 'generating',
    question: 'Are we generating real pipeline?',
    standfirst: 'Top-of-funnel effort, the meetings it books, and the open pipeline it builds.',
    tab: 'sales',
    tabLabel: 'Sales Activity',
    body: [
      'Open pipeline for the year stands at {{MAIN-17|fyActual}} — {{MAIN-17|fyCoverage}} of ' +
        'the {{MAIN-17|fyTarget}} coverage target, which is net of {{MAIN-17|fyClosedWon}} ' +
        'already closed-won, so we are currently {{MAIN-17|fyPaceWord}}. The ' +
        'cumulative-vs-target chart on Sales Activity shows where the gap opens up.',
      'Top-of-funnel effort: outbound ran at {{MAIN-04|count}} completed touches (emails, ' +
        'calls and LinkedIn) over the last 30 days — {{MAIN-04|wow}} versus the prior week ' +
        'and {{MAIN-04|mom}} versus the prior four weeks — and we booked {{MAIN-01|count}} ' +
        'new meetings over the same window.',
    ],
    strategic:
      'Pipeline is built in a chain — outbound effort becomes booked meetings, meetings become ' +
      'open pipeline — so the conversion between those links, not the raw volume of any one of ' +
      'them, is what decides whether the coverage gap closes.',
    highlights: [
      { label: 'Open pipeline FY', token: 'MAIN-17|fyActual' },
      { label: 'Coverage of target', token: 'MAIN-17|fyCoverage' },
      { label: 'Remaining target', token: 'MAIN-17|fyTarget' },
      { label: 'Outbound (30d)', token: 'MAIN-04|count' },
      { label: 'Outbound WoW', token: 'MAIN-04|wow' },
      { label: 'Meetings booked (30d)', token: 'MAIN-01|count' },
    ],
  },
  {
    id: 'closing',
    question: 'Are we closing better deals?',
    standfirst: 'Deal quality, win rate, cycle time and forecast reliability.',
    tab: 'sales',
    tabLabel: 'Sales Activity',
    body: [
      'Deal quality right now: the rolling five-quarter average closed-won deal is ' +
        '{{MAIN-12|qtrUsd}}. That is the smoothed "line to watch" — it filters out any single ' +
        'noisy quarter — and the quarter-by-quarter path on Sales Activity shows whether ' +
        'the trend is climbing toward the up-market deals we want.',
      'Win rate is {{MAIN-24}}, running at {{MAIN-24|pctTarget}} of the 30% target, and the ' +
        'average sales cycle is {{MAIN-20|days}} from creation to close.',
      'When we miss, the same tab says why: deal slippage flags which committed deals ' +
        'pushed their close date, and loss-by-reason groups the losses so the top one or ' +
        'two reasons are obvious.',
    ],
    strategic:
      'A larger average deal only compounds into revenue if win rate and cycle time hold, so ' +
      'deal velocity — not any single metric — is what turns a bigger pipeline into faster cash.',
    highlights: [
      { label: 'Avg deal (rolling 5q)', token: 'MAIN-12|qtrUsd' },
      { label: 'Win rate', token: 'MAIN-24' },
      { label: 'vs 30% target', token: 'MAIN-24|pctTarget' },
      { label: 'Sales cycle', token: 'MAIN-20|days' },
    ],
  },
  {
    id: 'spending',
    question: 'Are we spending efficiently?',
    standfirst: 'What marketing sources, what it costs, and how AEs pace to quota.',
    tab: 'roi',
    tabLabel: 'ROI',
    body: [
      'Marketing is sourcing {{MAIN-32|total}} of pipeline right now, broken out by channel ' +
        'on the Marketing tab alongside which sources actually convert and close — not just ' +
        'which generate volume.',
      'The ROI tab carries the efficiency headline once finance data is loaded: customer ' +
        'acquisition cost is {{MAIN-38}}, the LTV:CAC ratio is {{MAIN-39}}, and marketing ' +
        'spend is {{MAIN-37}} of CAC, plus cost-per-lead and cost-per-opportunity by ' +
        'channel. The AE actual-vs-quota bars bring it back to people. (Values read "—" ' +
        'here until the monthly finance upload lands.)',
    ],
    strategic:
      'Efficient growth is a channel-mix decision: concentrate spend where LTV:CAC and ' +
      'payback are strongest and starve the channels that only manufacture volume.',
    highlights: [
      { label: 'Mkt-sourced pipeline', token: 'MAIN-32|total' },
      { label: 'CAC', token: 'MAIN-38' },
      { label: 'LTV : CAC', token: 'MAIN-39' },
      { label: 'Mkt spend % of CAC', token: 'MAIN-37' },
    ],
  },
];
